import { useRef, useState, useEffect } from "react"

import { DrawIoEmbed, DrawIoEmbedRef } from "react-drawio"

import Button from "../../button/button"
import CustomSelect, {
  SelectOption,
} from "../../customSelect/customSelect"

import EditorOverlay from "./EditorOverlay"

import type { ContentInsertType } from "../types"
import {
  DIAGRAM_CONTENT_TYPES,
  DRAWIO_EMBED_URL_PARAMS,
  diagramTypeLabel,
} from "../utils/diagramConstants"
import {
  buildDiagramExportAction,
  isValidDiagramExportData,
} from "../utils/diagramExport"
import type { DiagramSavePayload } from "../utils/diagramNodeAttrs"

type DrawioEditorModalProps = {
  isOpen: boolean
  onClose: () => void
  onSave: (payload: DiagramSavePayload) => void
  initialXml?: string
  initialContentType?: ContentInsertType
  mode?: "create" | "edit"
}

const CONTENT_TYPE_OPTIONS: SelectOption[] = DIAGRAM_CONTENT_TYPES.map(
  (value) => ({
    value,
    label: diagramTypeLabel(value),
  })
)

const DrawioEditorModal = ({
  isOpen,
  onClose,
  onSave,
  initialXml,
  initialContentType = "geometry",
  mode = "create",
}: DrawioEditorModalProps) => {
  const drawioRef = useRef<DrawIoEmbedRef>(null)
  const [contentType, setContentType] =
    useState<ContentInsertType>(initialContentType)
  const [isEditorReady, setIsEditorReady] = useState(false)

  useEffect(() => {
    if (isOpen) {
      setContentType(initialContentType)
      setIsEditorReady(false)
    }
  }, [isOpen, initialContentType])

  const exportDiagram = () => {
    if (!isEditorReady) return
    drawioRef.current?.exportDiagram(buildDiagramExportAction())
  }

  const handleExport = (data: { data: string; xml?: string }) => {
    if (!isValidDiagramExportData(data)) return

    onSave({
      src: data.data,
      diagramXml: data.xml ?? "",
      contentType,
    })
    onClose()
  }

  const selectedType =
    CONTENT_TYPE_OPTIONS.find((option) => option.value === contentType) ?? null

  const isLegacyEdit = mode === "edit" && !initialXml

  return (
    <EditorOverlay
      isOpen={isOpen}
      onClose={onClose}
      className='tiptap-editor__drawio-modal'
    >
      <div className='tiptap-editor__drawio-header'>
        <div>
          <h3>{mode === "edit" ? "Edit diagram" : "Insert diagram"}</h3>
          <p>
            Draw shapes, graphs, or labels. Export when finished to insert into
            the question.
          </p>
          {isLegacyEdit && (
            <p className='tiptap-editor__drawio-warning'>
              This diagram has no saved source. Exporting will replace the image
              only.
            </p>
          )}
        </div>

        <div className='tiptap-editor__drawio-actions'>
          <Button type='secondary' text='Cancel' onClick={onClose} />
          <Button
            type='secondary'
            text='Export to editor'
            onClick={exportDiagram}
            disabled={!isEditorReady}
          />
        </div>
      </div>

      <div className='tiptap-editor__drawio-toolbar'>
        <label className='tiptap-editor__drawio-type-label' htmlFor='diagram-type'>
          Diagram type
        </label>
        <div className='tiptap-editor__drawio-type-select'>
          <CustomSelect
            id='diagram-type'
            data={CONTENT_TYPE_OPTIONS}
            value={selectedType}
            placeholder='Select type'
            onChange={(option) => {
              const next = option as SelectOption | null
              if (next?.value === "geometry" || next?.value === "graph") {
                setContentType(next.value)
              }
            }}
          />
        </div>
      </div>

      <div className='tiptap-editor__drawio-canvas'>
        <DrawIoEmbed
          ref={drawioRef}
          xml={initialXml}
          onLoad={() => setIsEditorReady(true)}
          onExport={handleExport}
          urlParameters={DRAWIO_EMBED_URL_PARAMS}
        />
      </div>
    </EditorOverlay>
  )
}

export default DrawioEditorModal
